
/**
 * Transaction Row Component
 *
 * Enhanced row display focusing on "Real and Relevant" Plaid data.
 * Highlights Merchant (Logo+Name), Category (PFC), and Payment Details.
 * Supports inline editing via double-click on merchant and category cells.
 */

import React, { memo, useState, useRef, useEffect, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Tr,
  Td,
  Checkbox,
  Badge,
  IconButton,
  Text,
  Tooltip,
  HStack,
  VStack,
  Avatar,
  Icon,
  Box,
  Input,
  useColorModeValue,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import {
  MdArrowForward,
  MdStore,
  MdLanguage,
  MdCreditCard,
  MdAccessTime,
  MdEdit,
  MdUndo,
} from 'react-icons/md';
import { PlaidTransaction, formatAccountDisplay } from '../../types/transactions';
import { CategorySelector } from './CategorySelector';
import { getFlowDirection, getCheckNumber } from '../../utils/payment-type';
import { getVendorDisplayLabel } from '../../utils/vendor-label';

interface CategoryOption {
  primary: string;
  detailed: string;
}

interface TransactionRowProps {
  transaction: PlaidTransaction;
  normalizedMerchant?: string;
  isSelected: boolean;
  onSelect: (id: string) => void;
  onEdit?: (transaction: PlaidTransaction) => void;
  onInlineSave?: (id: string, updates: { merchant?: string; pfc_primary?: string; pfc_detailed?: string }) => Promise<void>;
  onSaveVendor?: (id: string, vendor: string) => Promise<void>;
  onRevert?: (transaction: PlaidTransaction) => void;
  availableCategories?: CategoryOption[];
  isRecentlyEdited?: boolean;
  /**
   * Registers/unregisters this row's "commit the in-progress inline edit"
   * function with an ancestor (TransactionsExplorer, via TransactionTable),
   * so pagination controls can flush a pending edit before changing page
   * instead of unmounting the row — and losing the edit — mid-keystroke.
   * Called with a function while editingField is non-null, and with null
   * whenever editing ends (save, cancel, or unmount). Returns true once
   * it's safe to navigate away (nothing pending, or the save completed and
   * closed the editor), false if the editor is still open (e.g. the save
   * failed and left it open for retry — see saveVendorEdit).
   */
  onEditingActiveChange?: (commit: (() => Promise<boolean>) | null) => void;
  /** Renders the Check # cell after Date — must match TransactionTable's showCheckNumber. */
  showCheckNumber?: boolean;
}

const flashAnimation = keyframes`
  0% { background-color: var(--chakra-colors-cyan-900); }
  100% { background-color: transparent; }
`;

const TransactionRow: React.FC<TransactionRowProps> = ({
  transaction,
  normalizedMerchant,
  isSelected,
  onSelect,
  onEdit,
  onInlineSave,
  onSaveVendor,
  onRevert,
  availableCategories,
  isRecentlyEdited,
  onEditingActiveChange,
  showCheckNumber = false,
}) => {
  // Inline edit state
  const [editingField, setEditingField] = useState<null | 'merchant' | 'category' | 'vendor'>(null);
  const [editMerchant, setEditMerchant] = useState('');
  const [editVendor, setEditVendor] = useState('');
  const [editPfcPrimary, setEditPfcPrimary] = useState('');
  const [editPfcDetailed, setEditPfcDetailed] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const merchantInputRef = useRef<HTMLInputElement>(null);
  const vendorInputRef = useRef<HTMLInputElement>(null);
  const categoryRef = useRef<HTMLDivElement>(null);

  const handleCheckboxChange = () => {
    onSelect(transaction.id);
  };

  // --- Helpers ---

  const formatAmount = (val: number, cur: string | null) => {
      return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: cur || 'USD',
      }).format(Math.abs(val));
  };

  const formatDate = (dateStr: string) => {
      return new Date(dateStr).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
      });
  };

  // Payment Channel Icon
  const getChannelIcon = () => {
      switch(transaction.paymentChannel) {
          case 'online': return MdLanguage;
          case 'in store': return MdStore;
          default: return MdCreditCard;
      }
  };

  // Plaid convention: amount > 0 = debit/expense (red, -), amount < 0 = credit/income (green, +)
  const amountColor = transaction.amount < 0 ? 'green.500' : transaction.amount > 0 ? 'red.500' : 'gray.400';
  const vendorDisplayLabel = getVendorDisplayLabel({
    normalizedMerchant,
    normalizationStatus: transaction.normalizationStatus,
    merchantName: transaction.merchantName,
    name: transaction.name,
  });

  // Color mode aware colors
  const hoverBg = useColorModeValue('gray.100', 'xacto.mutedNavy');
  const selectedBg = useColorModeValue('gray.100', 'xacto.mutedNavy');
  const detailedColor = useColorModeValue('black', 'gray.400');

  // --- Inline Edit Handlers ---

  const startMerchantEdit = useCallback(() => {
    if (!onInlineSave) return;
    setEditMerchant(transaction.merchantName || transaction.name || '');
    setEditingField('merchant');
  }, [onInlineSave, transaction.merchantName, transaction.name]);

  const startCategoryEdit = useCallback(() => {
    if (!onInlineSave) return;
    setEditPfcPrimary(transaction.personalFinanceCategoryPrimary || '');
    setEditPfcDetailed(transaction.personalFinanceCategoryDetailed || '');
    setEditingField('category');
  }, [onInlineSave, transaction.personalFinanceCategoryPrimary, transaction.personalFinanceCategoryDetailed]);

  const cancelEdit = useCallback(() => {
    setEditingField(null);
  }, []);

  const startVendorEdit = useCallback(() => {
    if (!onSaveVendor) return;
    setEditVendor(normalizedMerchant || '');
    setEditingField('vendor');
  }, [onSaveVendor, normalizedMerchant]);

  const saveVendorEdit = useCallback(async () => {
    if (!onSaveVendor || isSaving) return;
    const trimmed = editVendor.trim();
    if (trimmed !== (normalizedMerchant || '')) {
      setIsSaving(true);
      try {
        await onSaveVendor(transaction.id, trimmed);
      } finally {
        setIsSaving(false);
      }
    }
    setEditingField(null);
  }, [onSaveVendor, editVendor, normalizedMerchant, transaction.id, isSaving]);

  const handleVendorKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') saveVendorEdit();
    else if (e.key === 'Escape') cancelEdit();
  };

  const saveMerchantEdit = useCallback(async () => {
    if (!onInlineSave || isSaving) return;
    const trimmed = editMerchant.trim();
    if (trimmed && trimmed !== (transaction.merchantName || transaction.name)) {
      setIsSaving(true);
      await onInlineSave(transaction.id, { merchant: trimmed });
      setIsSaving(false);
    }
    setEditingField(null);
  }, [onInlineSave, editMerchant, transaction.id, transaction.merchantName, transaction.name, isSaving]);

  const saveCategoryEdit = useCallback(async () => {
    if (!onInlineSave || isSaving) return;
    const changed =
      editPfcPrimary !== (transaction.personalFinanceCategoryPrimary || '') ||
      editPfcDetailed !== (transaction.personalFinanceCategoryDetailed || '');
    if (changed && editPfcPrimary && editPfcDetailed) {
      setIsSaving(true);
      await onInlineSave(transaction.id, { pfc_primary: editPfcPrimary, pfc_detailed: editPfcDetailed });
      setIsSaving(false);
    }
    setEditingField(null);
  }, [onInlineSave, editPfcPrimary, editPfcDetailed, transaction.id, transaction.personalFinanceCategoryPrimary, transaction.personalFinanceCategoryDetailed, isSaving]);

  // Focus inputs when entering edit mode
  useEffect(() => {
    if (editingField === 'merchant' && merchantInputRef.current) {
      merchantInputRef.current.focus();
      merchantInputRef.current.select();
    }
    if (editingField === 'vendor' && vendorInputRef.current) {
      vendorInputRef.current.focus();
      vendorInputRef.current.select();
    }
  }, [editingField]);

  // Close category editor on outside click
  useEffect(() => {
    if (editingField !== 'category') return;
    const handleClickOutside = (e: MouseEvent) => {
      if (categoryRef.current && !categoryRef.current.contains(e.target as Node)) {
        saveCategoryEdit();
      }
    };
    // Delay to prevent immediate close on the double-click that opened it
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editingField, saveCategoryEdit]);

  const handleMerchantKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveMerchantEdit();
    } else if (e.key === 'Escape') {
      cancelEdit();
    }
  };

  // Commits whichever field is currently being edited, same as pressing
  // Enter/blurring — used by the pagination "flush before navigating away"
  // guard (onEditingActiveChange below), not by any in-row UI.
  const commitPendingEdit = useCallback(async (): Promise<boolean> => {
    if (editingField === 'vendor') {
      // saveVendorEdit only clears editingField on success (or a no-op
      // save); a thrown/rejected save leaves it set to 'vendor' so the
      // input stays open with the user's text intact for retry.
      try {
        await saveVendorEdit();
        return true;
      } catch {
        return false;
      }
    }
    if (editingField === 'merchant') {
      // handleInlineSave (the underlying onInlineSave) catches its own
      // errors and toasts, so this always resolves and always closes the
      // editor — unlike the vendor path above.
      await saveMerchantEdit();
      return true;
    }
    if (editingField === 'category') {
      await saveCategoryEdit();
      return true;
    }
    return true; // nothing being edited — always safe to navigate
  }, [editingField, saveVendorEdit, saveMerchantEdit, saveCategoryEdit]);

  // Register/unregister this row's commit function with the ancestor
  // whenever editingField changes, so exactly one "flush" function is live
  // at a time (whichever row most recently entered edit mode).
  useEffect(() => {
    if (!onEditingActiveChange) return;
    onEditingActiveChange(editingField ? commitPendingEdit : null);
    return () => onEditingActiveChange(null);
  }, [editingField, onEditingActiveChange, commitPendingEdit]);

  return (
    <Tr
      _hover={{ bg: hoverBg }}
      bg={isSelected ? selectedBg : 'transparent'}
      borderLeftWidth={transaction.isModified ? "4px" : "0px"}
      borderLeftColor={transaction.isModified ? "orange.400" : "transparent"}
      boxShadow={
        transaction.amount < 0
          ? 'inset 3px 0 0 0 rgba(34, 197, 94, 0.5)'
          : transaction.amount > 0
          ? 'inset 3px 0 0 0 rgba(239, 68, 68, 0.4)'
          : undefined
      }
      transition="all 0.2s"
      animation={isRecentlyEdited ? `${flashAnimation} 1.5s ease-out` : undefined}
    >
      {/* 1. Checkbox */}
      <Td w="40px" px={2}>
        <Checkbox
          isChecked={isSelected}
          onChange={handleCheckboxChange}
          colorScheme="cyan"
          borderColor="xacto.borderGray"
        />
      </Td>

      {/* 2. Date */}
      <Td w="100px" px={2}>
        <Text fontSize="sm" color="text.secondary">
          {formatDate(transaction.date)}
        </Text>
      </Td>

      {showCheckNumber && (
        <Td w="90px" px={2} textAlign="right">
          <Text fontSize="sm" color="text.secondary" fontFamily="mono">
            {getCheckNumber(transaction) ?? '—'}
          </Text>
        </Td>
      )}

      {/* 3. Merchant (Logo + Identity) - Double-click to inline edit */}
      <Td w="250px" px={2} onDoubleClick={startMerchantEdit} cursor={onInlineSave ? 'default' : undefined}>
        {editingField === 'merchant' ? (
          <Input
            ref={merchantInputRef}
            value={editMerchant}
            onChange={(e) => setEditMerchant(e.target.value)}
            onBlur={saveMerchantEdit}
            onKeyDown={handleMerchantKeyDown}
            size="sm"
            bg="bg.surface"
            borderColor="xacto.electricCyan"
            _focus={{ borderColor: 'xacto.electricCyan', boxShadow: '0 0 0 1px var(--chakra-colors-cyan-400)' }}
            isDisabled={isSaving}
          />
        ) : (
          <HStack spacing={3}>
              <Avatar
                  size="sm"
                  name={transaction.merchantName || transaction.name}
                  src={transaction.logoUrl || undefined}
                  bg="xacto.lightNavy"
                  color="accent.primary"
              />
              <VStack align="start" spacing={0} maxW="100%" overflow="hidden">
                  <Text
                      fontSize="sm"
                      fontWeight="bold"
                      color="text.primary"
                      noOfLines={1}
                  >
                      {transaction.merchantName || transaction.name}
                  </Text>
                  <Text fontSize="xs" color="gray.500" noOfLines={1}>
                      {transaction.merchantName && transaction.merchantName !== transaction.name
                          ? transaction.name
                          : transaction.originalDescription || ''}
                  </Text>
              </VStack>
          </HStack>
        )}
      </Td>

      {/* 3b. Vendor (normalized merchant) — double-click to edit */}
      <Td
        w="180px"
        px={2}
        onDoubleClick={startVendorEdit}
        role={onSaveVendor ? 'button' : undefined}
        title={onSaveVendor ? 'Double-click to edit vendor' : undefined}
        _hover={onSaveVendor ? { bg: 'whiteAlpha.50' } : undefined}
        cursor={onSaveVendor ? 'text' : undefined}
      >
        {editingField === 'vendor' ? (
          <Input
            ref={vendorInputRef}
            value={editVendor}
            onChange={(e) => setEditVendor(e.target.value)}
            onBlur={saveVendorEdit}
            onKeyDown={handleVendorKeyDown}
            size="sm"
            placeholder="vendor name…"
            bg="bg.surface"
            borderColor="xacto.electricCyan"
            _focus={{ borderColor: 'xacto.electricCyan', boxShadow: '0 0 0 1px var(--chakra-colors-cyan-400)' }}
            isDisabled={isSaving}
          />
        ) : (
          <Text fontSize="sm" color={normalizedMerchant ? 'text.primary' : 'gray.500'} noOfLines={1}>
            {vendorDisplayLabel}
          </Text>
        )}
      </Td>

      {/* 4. Amount */}
      <Td w="120px" px={2}>
        <Text fontSize="sm" fontWeight="bold" color={amountColor}>
           {transaction.amount < 0 ? '+' : transaction.amount > 0 ? '-' : ''}
           {formatAmount(transaction.amount, transaction.isoCurrencyCode)}
        </Text>
      </Td>

      {/* 4b. Flow */}
      <Td w="90px" px={2}>
        {(() => {
          const dir = getFlowDirection(transaction);
          return (
            <Badge
              colorScheme={dir === 'INCOME' ? 'green' : 'red'}
              variant="subtle"
              fontSize="xs"
            >
              {dir === 'INCOME' ? 'INCOME' : 'EXPENSE'}
            </Badge>
          );
        })()}
      </Td>

      {/* 5. Category (PFC) - Double-click to inline edit */}
      <Td px={2} onDoubleClick={startCategoryEdit} cursor={onInlineSave ? 'default' : undefined}>
        {editingField === 'category' ? (
          <Box
            ref={categoryRef}
            bg="bg.primary"
            borderWidth="1px"
            borderColor="xacto.electricCyan"
            borderRadius="md"
            p={3}
            shadow="lg"
            minW="280px"
          >
            <CategorySelector
              initialPrimary={editPfcPrimary}
              initialDetailed={editPfcDetailed}
              availableCategories={availableCategories || []}
              onChange={(p, d) => {
                setEditPfcPrimary(p);
                setEditPfcDetailed(d);
              }}
            />
            <HStack mt={2} justify="flex-end" spacing={2}>
              <Text
                fontSize="xs"
                color="text.muted"
                cursor="pointer"
                onClick={cancelEdit}
                _hover={{ color: 'text.secondary' }}
              >
                Cancel
              </Text>
              <Text
                fontSize="xs"
                color="accent.primary"
                cursor="pointer"
                fontWeight="bold"
                onClick={saveCategoryEdit}
                _hover={{ textDecoration: 'underline' }}
              >
                Save
              </Text>
            </HStack>
          </Box>
        ) : (
          <VStack align="start" spacing={1}>
              <HStack>
                  {transaction.isModified && (
                      <Badge colorScheme="orange" variant="solid" fontSize="10px" px={1}>EDITED</Badge>
                  )}
                  <Badge
                  colorScheme="cyan"
                  variant="solid"
                  fontSize="10px"
                  borderRadius="full"
                  px={2}
              >
                  {transaction.personalFinanceCategoryPrimary?.replace(/_/g, ' ') || 'UNCATEGORIZED'}
              </Badge>
              </HStack>
              <Text fontSize="xs" color={detailedColor} noOfLines={1}>
                  {transaction.personalFinanceCategoryDetailed?.replace(/_/g, ' ') || '-'}
              </Text>
          </VStack>
        )}
      </Td>

      {/* 6. Payment (Channel & Status) */}
      <Td w="100px" px={2}>
        <HStack spacing={2} color="gray.400">
            <Tooltip label={`Channel: ${transaction.paymentChannel || 'Unknown'}`}>
                <span><Icon as={getChannelIcon()} boxSize={5} /></span>
            </Tooltip>

            {transaction.pending && (
                 <Tooltip label="Pending Transaction">
                    <span><Icon as={MdAccessTime} color="orange.400" boxSize={5} /></span>
                 </Tooltip>
            )}
        </HStack>
      </Td>

      {/* 7. Account */}
      <Td w="150px" px={2}>
        <Text fontSize="xs" color="text.secondary" noOfLines={1}>
             {formatAccountDisplay(transaction.plaidAccount)}
        </Text>
      </Td>

      {/* 8. Actions */}
      <Td w="50px" px={2}>
        <HStack spacing={0}>
            {/* Revert Button (only for modified transactions) */}
            {onRevert && transaction.isModified && (
                <Tooltip label="Revert to original">
                    <IconButton
                        aria-label="Revert transaction"
                        icon={<Icon as={MdUndo} />}
                        size="sm"
                        variant="ghost"
                        color="gray.400"
                        _hover={{ bg: hoverBg, color: 'accent.primary' }}
                        onClick={() => onRevert(transaction)}
                    />
                </Tooltip>
            )}

            {/* Edit Button (Conditional) */}
            {onEdit && (
                <Tooltip label="Edit in modal">
                    <IconButton
                        aria-label="Edit transaction"
                        icon={<Icon as={MdEdit} />}
                        size="sm"
                        variant="ghost"
                        color="orange.400"
                        _hover={{ bg: hoverBg }}
                        onClick={() => onEdit(transaction)}
                    />
                </Tooltip>
            )}

            <IconButton
              as={RouterLink}
              to={`/transaction/${transaction.originalPlaidTransactionId || transaction.id}`}
              aria-label="View details"
              icon={<MdArrowForward />}
              size="sm"
              variant="ghost"
              color="accent.primary"
              _hover={{ bg: hoverBg }}
            />
        </HStack>
      </Td>
    </Tr>
  );
};

export default memo(TransactionRow, (prev, next) => {
  return (
    prev.transaction === next.transaction &&
    prev.normalizedMerchant === next.normalizedMerchant &&
    prev.isSelected === next.isSelected &&
    prev.isRecentlyEdited === next.isRecentlyEdited &&
    prev.onEdit === next.onEdit &&
    prev.onInlineSave === next.onInlineSave &&
    prev.onSaveVendor === next.onSaveVendor &&
    prev.onRevert === next.onRevert &&
    prev.onEditingActiveChange === next.onEditingActiveChange
  );
});
